import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatPhone } from '@/lib/helpers';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BatizadoRegistroDialog({ state, setState, onSaved, churches }) {
  const [saving, setSaving] = useState(false);
  const [lastSavedName, setLastSavedName] = useState('');
  const save = async () => {
    const comumFinal = state.comumOutra ? (state.comumCustom || '').trim() : (state.comum || '').trim();
    if (!state.nome.trim()) { toast.error('Nome do batizado é obrigatório'); return; }
    if (state.idade === '' || state.idade === null || state.idade === undefined) { toast.error('Idade é obrigatória'); return; }
    if (!String(state.whatsapp || '').trim()) { toast.error('WhatsApp é obrigatório'); return; }
    if (!comumFinal) { toast.error('Comum é obrigatória'); return; }
    setSaving(true);
    try {
      const url = state.editingId ? `${API}/batismos/registros/${state.editingId}` : `${API}/batismos/${state.batismoId}/registros`;
      const r = await fetch(url, { method: state.editingId ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nome: state.nome, idade: Number(state.idade), whatsapp: state.whatsapp, endereco: state.endereco || '', endereco_place: state.enderecoPlace || null, comum: comumFinal }) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao salvar batizado');
      toast.success(state.editingId ? 'Batizado atualizado' : 'Batizado registrado');
      onSaved();
      if (!state.editingId) {
        setLastSavedName(data.nome || state.nome.trim());
        setState(prev => ({ ...prev, nome: '', idade: '', whatsapp: '', endereco: '', enderecoPlace: null, comum: '', comumOutra: false, comumCustom: '' }));
      } else {
        setState(prev => ({ ...prev, open: false }));
      }
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <Dialog open={state.open} onOpenChange={(open) => setState(prev => ({ ...prev, open }))}>
      <DialogContent className="max-w-3xl max-sm:!fixed max-sm:!inset-0 max-sm:!left-0 max-sm:!top-0 max-sm:!m-0 max-sm:!h-[100dvh] max-sm:!max-h-none max-sm:!w-screen max-sm:!max-w-none max-sm:![transform:none] max-sm:!rounded-none max-sm:!border-0 max-sm:!p-4 max-sm:!flex max-sm:!flex-col max-sm:!overflow-hidden" data-testid="batismo-registro-modal">
        <DialogHeader className="max-sm:flex-shrink-0">
          <DialogTitle>{state.editingId ? 'Editar Batizado' : 'Registrar Batizado'}</DialogTitle>
          <DialogDescription>Todos os campos são obrigatórios.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 max-sm:flex-1 max-sm:min-h-0 max-sm:overflow-y-auto max-sm:pb-4">
          {lastSavedName && !state.editingId && (
            <div className="sm:hidden rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="batizado-last-saved-mobile">
              Último cadastrado: <strong>{lastSavedName}</strong>
            </div>
          )}
          <div className="order-1">
            <Label>Nome *</Label>
            <Input value={state.nome} onChange={(e) => setState(prev => ({ ...prev, nome: e.target.value }))} data-testid="batizado-nome-input" />
          </div>
          <div className="order-2 sm:order-4">
            <Label>Endereço</Label>
            <AddressAutocompleteInput value={state.endereco || ''} onChange={(value, place) => setState(prev => ({ ...prev, endereco: value, enderecoPlace: place || null }))} placeholder="Digite o endereço" testId="batizado-endereco-input" />
          </div>
          <div className="order-3 sm:order-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Idade *</Label>
              <Input type="number" inputMode="numeric" pattern="[0-9]*" min="0" max="120" value={state.idade} onChange={(e) => setState(prev => ({ ...prev, idade: e.target.value.replace(/\D/g, '') }))} data-testid="batizado-idade-input" />
            </div>
            <div>
              <Label>WhatsApp *</Label>
              <Input type="tel" inputMode="numeric" pattern="[0-9]*" value={state.whatsapp} onChange={(e) => setState(prev => ({ ...prev, whatsapp: formatPhone(e.target.value) }))} placeholder="(00) 00000-0000" data-testid="batizado-whatsapp-input" />
            </div>
            <div>
              <Label>Comum *</Label>
              <select value={state.comumOutra ? '__outra__' : state.comum} onChange={(e) => { if (e.target.value === '__outra__') setState(prev => ({ ...prev, comumOutra: true, comum: '', comumCustom: prev.comumCustom || '' })); else setState(prev => ({ ...prev, comumOutra: false, comum: e.target.value, comumCustom: '' })); }} className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-testid="batizado-comum-select">
                <option value="">Selecione uma comum</option>
                {(churches || []).map(church => <option key={church.church_id} value={church.name}>{church.name}</option>)}
                <option value="__outra__">Outra</option>
              </select>
            </div>
          </div>
          {state.comumOutra && (
            <div className="order-4 sm:order-3">
              <Label>Outra comum *</Label>
              <Input value={state.comumCustom} onChange={(e) => setState(prev => ({ ...prev, comumCustom: e.target.value }))} data-testid="batizado-comum-outra-input" />
            </div>
          )}
          <Button onClick={save} disabled={saving} className="order-5 w-full bg-[#069668] hover:bg-[#057f59] max-sm:mt-auto" data-testid="batizado-save-button">{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}